import { useEffect, useMemo, useState } from 'react';
import {
  ScamWarning,
  VerificationFailed,
  VerificationPending,
  VerificationSuccessful,
  VerifyYourIdentity,
} from '@/components/common/popups';
import { AgeWarning } from '@/components/common/popups/AgeWarning';
import { useCheckStatus, useInitiateWithdrawCheck } from '@/api/queryHooks/useKycController';
import { AgeGroup, KycStatus } from '@/api/common.types';
import SockJsClient from 'react-stomp';
import { useReadLocalStorage } from 'usehooks-ts';
import { AuthUserResponse } from '@/api';
import { ACCESS_TOKEN, USER_DETAILS } from '@/constants/storage';
import { VerificationRetry } from '@/components/common/popups/VerificationRetry';
import { useVerifyStore } from '@/store/verify';
import { shallow } from 'zustand/shallow';

interface VerificationProps {
  onSuccessVerification: () => void;
  isSellFlow?: boolean;
}

interface BiometricKycStatus {
  checkId: string;
  status: KycStatus;
}

enum Steps {
  ScamWarning,
  AgeWarning,
  VerifyYourIdentity,
  VerificationResult,
}

enum VerificationStatus {
  Pending,
  Success,
  Failed,
  Retry,
  Rejected,
  MultipleVerification,
}

// Terminal/non-retryable statuses where the user cannot proceed with verification.
// Mirrors backend KycUserStatus.canRetry() = false; kept in sync deliberately.
const TERMINAL_KYC_STATUSES: ReadonlySet<KycStatus> = new Set([
  KycStatus.REJECT,
  KycStatus.FRAUD,
  KycStatus.MULE,
  KycStatus.UNDER_AGE,
  KycStatus.CONTACT_SUPPORT,
  KycStatus.SELL_ONLY_CONTACT_SUPPORT,
  KycStatus.RETRY_ATTEMPTS_EXCEEDED,
]);

export const Verification = ({ onSuccessVerification, isSellFlow }: VerificationProps) => {
  const [step, setStep] = useState(isSellFlow ? Steps.VerifyYourIdentity : Steps.ScamWarning);
  const [status, setStatus] = useState<VerificationStatus>(VerificationStatus.Pending);

  const isWaitingForResult = step === Steps.VerificationResult && status === VerificationStatus.Pending;

  const { data: kycStatus, refetch: refetchKycStatus } = useCheckStatus({
    onSuccess(data) {
      if (data.status === KycStatus.PENDING) {
        setVerificationPending();
      }
    },
    refetchInterval: isWaitingForResult ? 5000 : false,
  });
  const [verificationId, setCheckId] = useVerifyStore((state) => [state.checkId, state.setCheckId], shallow);
  const { mutateAsync: initiateWithdrawCheck } = useInitiateWithdrawCheck();

  const isBiometric = kycStatus?.status === KycStatus.BIOMETRIC_REQUIRED;

  // For biometric (BUY_CRYPTO) flow, initiate the withdraw check on the server to get a verificationId.
  // For registration flow, generate a local UUID.
  const [currentVerificationId, setCurrentVerificationId] = useState<string>(verificationId);
  useEffect(() => {
    if (currentVerificationId) return;

    if (isBiometric) {
      initiateWithdrawCheck().then((resp) => {
        setCheckId(resp.verificationId);
        setCurrentVerificationId(resp.verificationId);
      });
    } else {
      const newId = crypto.randomUUID();
      setCheckId(newId);
      setCurrentVerificationId(newId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Polling fallback for registration KYC: if the websocket message is missed
  // (e.g. connection drop), the polled status will still update the UI.
  // Not applicable for biometric flow since /kyc/status returns overall user status,
  // not the per-transaction biometric check result.
  useEffect(() => {
    if (!isWaitingForResult || isBiometric || !kycStatus?.status) return;
    const polledStatus = kycStatus.status;
    if (
      polledStatus !== KycStatus.PENDING &&
      polledStatus !== KycStatus.EMPTY &&
      polledStatus !== KycStatus.INITIATED
    ) {
      changeViewBasedOnKycStatus(polledStatus);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kycStatus?.status, isWaitingForResult, isBiometric]);

  const userDetails = useReadLocalStorage<AuthUserResponse>(USER_DETAILS);
  const accessToken = useReadLocalStorage(ACCESS_TOKEN);

  function onScamWarningNext() {
    const nextStep =
      // eslint-disable-next-line no-nested-ternary
      kycStatus?.ageGroup === AgeGroup.ALLOWED_WITH_WARNING
        ? Steps.AgeWarning
        : kycStatus?.ageGroup === AgeGroup.ALLOWED
        ? Steps.VerifyYourIdentity
        : Steps.ScamWarning;

    setStep(nextStep);
  }

  function startAgain() {
    setStep(Steps.ScamWarning);
    setStatus(VerificationStatus.Pending);
  }

  function onComplete() {
    setStep(Steps.VerificationResult);
  }

  function setVerificationPending() {
    setStep(Steps.VerificationResult);
    setStatus(VerificationStatus.Pending);
  }

  function setVerificationRetry() {
    setStep(Steps.VerificationResult);
    setStatus(VerificationStatus.Retry);
  }

  function setVerificationFailed() {
    setStep(Steps.VerificationResult);
    setStatus(VerificationStatus.Failed);
  }

  function setVerificationSuccess() {
    setStep(Steps.VerificationResult);
    setStatus(VerificationStatus.Success);
  }

  function setVerificationRejected() {
    setStep(Steps.VerificationResult);
    setStatus(VerificationStatus.Rejected);
  }

  function setVerificationMultipleVerification() {
    setStep(Steps.VerificationResult);
    setStatus(VerificationStatus.MultipleVerification);
  }

  // Token generation failed (typically 409 CONFLICT for a non-retryable status).
  // Refetch status so we can route to the correct terminal modal instead of the generic Oops.
  async function onTokenError() {
    try {
      const refreshed = await refetchKycStatus();
      const refreshedStatus = refreshed.data?.status;
      if (refreshedStatus) {
        changeViewBasedOnKycStatus(refreshedStatus);
        return;
      }
    } catch {
      // fall through to generic failed state
    }
    setVerificationFailed();
  }

  function renderStep(step: number) {
    const verificationResult = (status: VerificationStatus) => {
      switch (status) {
        case VerificationStatus.Pending:
          return <VerificationPending description="Comparing photos…" />;
        case VerificationStatus.Success:
          return <VerificationSuccessful onClick={onSuccessVerification} />;
        case VerificationStatus.Retry:
          return <VerificationRetry onClick={startAgain} message={kycStatus?.failureMessage} />;
        case VerificationStatus.Rejected:
          return (
            <VerificationFailed
              status="rejected"
              message={kycStatus?.failureMessage}
              isDuplicate={kycStatus?.failureReason === 'DUPLICATE_ACCOUNT'}
            />
          );
        case VerificationStatus.MultipleVerification:
          return <VerificationFailed status="multipleVerification" />;
        default:
          return <VerificationFailed status="general" />;
      }
    };

    switch (step) {
      case Steps.AgeWarning:
        return <AgeWarning onClickNext={() => setStep(Steps.VerifyYourIdentity)} />;
      case Steps.VerifyYourIdentity:
        if (!currentVerificationId) {
          return <VerificationPending description="Initializing verification…" />;
        }
        return (
          <VerifyYourIdentity onComplete={onComplete} verificationId={currentVerificationId} onError={onTokenError} />
        );
      case Steps.VerificationResult:
        return verificationResult(status);
      default:
        return <ScamWarning onClickNext={onScamWarningNext} />;
    }
  }

  function changeViewBasedOnKycStatus(kycStatusValue: KycStatus) {
    // If status is initiated, it means the user has just completed the identity form
    // and the verification process has started,
    // but we haven't received any updates yet.
    //  We can simply return and wait for the next status update.
    if (kycStatusValue === KycStatus.INITIATED) return;

    switch (true) {
      case kycStatusValue === KycStatus.PENDING: {
        setVerificationPending();
        break;
      }
      case kycStatusValue === KycStatus.CLEAR:
      case kycStatusValue === KycStatus.BIOMETRIC_REQUIRED:
      case kycStatusValue === KycStatus.SELL_ONLY:
      case kycStatusValue === KycStatus.KYC_PASSED_LEVEL2:
      case kycStatusValue === KycStatus.KYC_PASSED_LEVEL3: {
        setVerificationSuccess();
        break;
      }
      case kycStatusValue === KycStatus.CONSIDER: {
        setVerificationRetry();
        break;
      }
      case kycStatusValue === KycStatus.REJECT:
      case kycStatusValue === KycStatus.FRAUD:
      case kycStatusValue === KycStatus.MULE:
      case kycStatusValue === KycStatus.UNDER_AGE: {
        setVerificationRejected();
        break;
      }
      case kycStatusValue === KycStatus.RETRY_ATTEMPTS_EXCEEDED: {
        setVerificationMultipleVerification();
        break;
      }
      case kycStatusValue === KycStatus.CONTACT_SUPPORT:
      case kycStatusValue === KycStatus.SELL_ONLY_CONTACT_SUPPORT: {
        setVerificationFailed();
        break;
      }
      default: {
        setVerificationFailed();
      }
    }
  }

  // Route to a terminal result modal as soon as we know the user's status — before they
  // walk through ScamWarning / VerifyYourIdentity and trigger a doomed token request.
  // Covers REJECT/FRAUD/MULE/UNDER_AGE/CONTACT_SUPPORT/SELL_ONLY_CONTACT_SUPPORT/RETRY_ATTEMPTS_EXCEEDED
  // plus PENDING (mid-review) so a refresh during review still lands on the pending modal.
  useEffect(() => {
    const currentStatus = kycStatus?.status;
    if (!currentStatus) return;
    if (currentStatus === KycStatus.PENDING || TERMINAL_KYC_STATUSES.has(currentStatus)) {
      changeViewBasedOnKycStatus(currentStatus);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kycStatus?.status]);

  return (
    <>
      <SockJsClient
        url={`${process.env.NEXT_PUBLIC_API_BASE_URL}/websocket?token=${accessToken}`}
        topics={[
          `/topic/onfido/status/${userDetails?.websocketId}`,
          `/topic/onfido/biometric/${userDetails?.websocketId}`,
        ]}
        onMessage={(kycStatusMsg: BiometricKycStatus | KycStatus, topic?: string) => {
          if (typeof kycStatusMsg === 'string') {
            // Overall user status. In biometric flow, BIOMETRIC_REQUIRED is the steady
            // state — any rebroadcast here would falsely trip VerificationSuccess before
            // the per-tx KycBuyCryptoEntity is CLEAR. Ignore it; rely on the biometric
            // topic for the success signal.
            if (isBiometric && kycStatusMsg === KycStatus.BIOMETRIC_REQUIRED) {
              return null;
            }
            return changeViewBasedOnKycStatus(kycStatusMsg);
          }
          if (typeof kycStatusMsg === 'object' && kycStatusMsg?.checkId) {
            setCheckId(kycStatusMsg.checkId);
            return changeViewBasedOnKycStatus(kycStatusMsg.status);
          }
          return null;
        }}
      />
      {renderStep(step)}
    </>
  );
};
